import { supabase } from '@/integrations/supabase/client';
import { serializeCatalog } from './eventCatalog';

let didSync = false;

export interface CatalogSyncResult {
  ok: boolean;
  upserted?: number;
  retired?: number;
  error?: string;
}

/**
 * Pushes the code catalog into `event_type_registry`.
 *
 * The registry is a MIRROR, not the source of truth (see activityCatalogView.ts).
 * It matters because analytics RPCs read it for category filters and retirement,
 * so a stale mirror silently skews those queries. This function returns its
 * outcome instead of swallowing it, so /admin/activity-catalog can report a
 * failed sync rather than showing a confidently wrong page.
 *
 * Server-side, `sync-event-catalog` requires CRON_SECRET, the service role key,
 * or a system-admin JWT.
 */
export async function runEventCatalogSync(): Promise<CatalogSyncResult> {
  try {
    const catalog = serializeCatalog();
    const { data, error } = await supabase.functions.invoke('sync-event-catalog', {
      body: { catalog },
    });
    if (error) return { ok: false, error: error.message };
    const result = data as { ok?: boolean; upserted?: number; retired?: number; error?: string } | null;
    if (result?.ok === false) return { ok: false, error: result.error ?? 'Sync rejected' };
    return { ok: true, upserted: result?.upserted, retired: result?.retired };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/** True when the signed-in user may run the sync (system admins only). */
export async function canSyncEventCatalog(): Promise<boolean> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id;
  if (!userId) return false;
  const { data: isAdmin } = await supabase.rpc('is_system_admin', { _user_id: userId });
  return isAdmin === true;
}

/**
 * Fire-and-forget sync at app boot. Runs at most once per page load, only for
 * system admins (the only role the function authorises), and never throws.
 * Failures are recorded on `lastBootSyncResult` so the catalog page can surface
 * them instead of leaving the operator to guess.
 */
let lastBootSyncResult: CatalogSyncResult | null = null;

export function getLastBootSyncResult(): CatalogSyncResult | null {
  return lastBootSyncResult;
}

export function syncEventCatalogOnce() {
  if (didSync) return;
  didSync = true;

  (async () => {
    try {
      if (!(await canSyncEventCatalog())) return; // not authorised — skip silently
      lastBootSyncResult = await runEventCatalogSync();
      if (!lastBootSyncResult.ok) {
        console.warn('[syncEventCatalog] sync failed:', lastBootSyncResult.error);
      }
    } catch {
      // absolute safety net — never throw
    }
  })();
}
